import React, { useContext, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import axios from '../../../../config/axios'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { Eye, Pencil, Trash2 } from 'lucide-react'
import PricesDisplay from '../../../../components/PricesDisplay'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import ConfirmationModal from '../../../../components/ModalConfirmation'
import { secondsToHHmm } from '../../../../utils/time'
import { StandardColumn, StandardDataTable, useColumnsState } from '../../../../components/StandardPage'

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: auto;
  width: 100%;
  padding-right: 10px;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #c0c0c0;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #ffffff;
    border-radius: 10px;
    border: 2px solid #c0c0c0;
  }
`

interface Service {
  id: number
  name: string
  code?: string
  category?: { id: number; name: string } | null
  type?: { id: number; name: string } | null
  sector?: { id: number; name: string; Area?: { id: number; name: string } | null } | null
  pricePerHour?: number
  prices?: Array<{
    id: number
    price: string
    unitOfMeasurement: {
      id: number
      name: string
    }
  }>
  deadlineInDays?: number
  executionTime?: number | null
  setupTime?: number | null
  costs?: Array<{
    id: number
    cost: string
    unitOfMeasurementId: number
    unitOfMeasurement: {
      id: number
      name: string
    }
  }>
  markup?: string | null
}

const servicesScreenKey = 'services'

const formatMarkup = (markup?: string | null) => {
  if (markup === null || markup === undefined || markup === '') return 'N/A'
  return `${Number(markup)}x`
}

const formatDeadline = (deadline?: number | null) => {
  if (!deadline) return 'N/A'
  return `${deadline} ${deadline === 1 ? 'dia útil' : 'dias úteis'}`
}

const formatTime = (value?: number | null) => {
  if (value == null) return 'N/A'
  const formatted = secondsToHHmm(value)
  return formatted ? formatted : 'N/A'
}

const buildServicesColumns = (): StandardColumn<Service>[] => [
  {
    id: 'name',
    label: 'Serviço',
  },
  {
    id: 'area',
    label: 'Área',
    accessor: (service) => service.sector?.Area?.name || (service as any).Sector?.Area?.name || 'N/A',
  },
  {
    id: 'sector',
    label: 'Setor',
    accessor: (service) => service.sector?.name || (service as any).Sector?.name || 'N/A',
  },
  {
    id: 'category',
    label: 'Categoria',
    accessor: (service) => service.category?.name || (service as any).Category_Stage?.name || 'N/A',
  },
  {
    id: 'type',
    label: 'Tipo',
    accessor: (service) => service.type?.name || (service as any).Type_Category_Stage?.name || 'N/A',
  },
  {
    id: 'code',
    label: 'Código',
    accessor: (service) => service.code || 'N/A',
  },
  {
    id: 'prices',
    label: 'Preço',
    sortable: false,
    exportValue: (service) =>
      (service.prices || (service as any).StagePrice || [])
        .map((price: any) => `${price.price} ${price.unitOfMeasurement?.name || ''}`.trim())
        .join('; '),
    cell: (service) => <PricesDisplay prices={service.prices || (service as any).StagePrice || []} maxWidth={200} />,
  },
  {
    id: 'costs',
    label: 'Custo',
    sortable: false,
    exportValue: (service) =>
      (service.costs || [])
        .map((cost: any) => `${cost.cost} ${cost.unitOfMeasurement?.name || ''}`.trim())
        .join('; '),
    cell: (service) =>
      service.costs && service.costs.length > 0 ? (
        <PricesDisplay
          prices={service.costs.map(c => ({ price: String(c.cost), unitOfMeasurement: c.unitOfMeasurement }))}
          maxWidth={200}
        />
      ) : <>N/A</>,
  },
  {
    id: 'markup',
    label: 'Markup',
    accessor: (service) => formatMarkup(service.markup),
  },
  {
    id: 'deadlineInDays',
    label: 'Prazo',
    accessor: (service) => formatDeadline(service.deadlineInDays),
  },
  {
    id: 'executionTime',
    label: 'Tempo execução',
    accessor: (service) => formatTime(service.executionTime),
  },
  {
    id: 'setupTime',
    label: 'Tempo setup',
    accessor: (service) => formatTime(service.setupTime),
  },
]

const StageServicesTable:React.FC = () => {
  const navigate = useNavigate()
  const context = useOutletContext<any>()
  const search = context?.search || ''
  const { refresh, setIsLoading, isLoading, appliedFilters } = context?.outletContext || {}

  const [services, setServices] = useState<Service[]>([])
  const [visibleCount, setVisibleCount] = useState<number>(50)
  const [totalServices, setTotalServices] = useState<number | undefined>(undefined)
  const [deleteTarget, setDeleteTarget] = useState<Service | null>(null)
  const [localRefresh, setLocalRefresh] = useState(0)
  const { setFeedback } = useContext(FeedbackContext)

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/services/service-stage/${deleteTarget.id}`)
      setFeedback({ message: 'Serviço excluído com sucesso!', isOpen: true, statusOk: true })
      setDeleteTarget(null)
      setLocalRefresh((prev) => prev + 1)
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao excluir serviço'
      setDeleteTarget(null)
      setFeedback({ message: errorMessage, isOpen: true, statusOk: false })
    }
  }

  const columns = useMemo(() => buildServicesColumns(), [])
  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(servicesScreenKey, columns)

  useEffect(() => {
    const getServices = async () => {
      if (setIsLoading) setIsLoading(true)
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/services/service-stage`, {
          params: {
            limit: visibleCount,
            offset: 0,
            search: search,
            filters: appliedFilters && appliedFilters.length > 0 ? JSON.stringify(appliedFilters) : undefined
          }
        })
        if(response.data) setServices(response.data)
        if (setIsLoading) setIsLoading(false)
        try {
          const resp = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/services/service-stage/count`, {
            params: {
              search: search,
              filters: appliedFilters && appliedFilters.length > 0 ? JSON.stringify(appliedFilters) : undefined
            }
          })
          if (resp && resp.data != null) {
            const value = typeof resp.data === 'number' ? resp.data : resp.data.totalItems ?? resp.data.total ?? undefined
            setTotalServices(value)
          }
        } catch (err) {
          setTotalServices(undefined)
        }
      } catch (error) {
        console.error(error)
        if (setIsLoading) setIsLoading(false)
      }
    }
    getServices()
  }, [visibleCount, search, refresh, appliedFilters, localRefresh])

  return (
    <TableWrapper>
      <StandardDataTable
        columns={columns}
        data={services}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        totalCount={typeof totalServices === 'number' ? totalServices : null}
        onLoadMore={() => setVisibleCount(prev => prev + 50)}
        hasMore={typeof totalServices === 'number' ? services.length < totalServices : false}
        isLoading={isLoading}
        countLabel='serviços'
        exportFileName='servicos'
        emptyMessage='Nenhum serviço encontrado.'
        rowActions={(service) => [
          { label: 'Visualizar', icon: <Eye size={16} />, onClick: () => navigate(`view/?id=${service.id}`) },
          { label: 'Editar', icon: <Pencil size={16} />, onClick: () => navigate(`/panel/service/edit/?id=${service.id}`) },
          { label: 'Excluir', icon: <Trash2 size={16} />, onClick: () => setDeleteTarget(service) },
        ]}
      />

      <ConfirmationModal
        isOpen={!!deleteTarget}
        title="Confirmar Exclusão"
        message={
          <div>
            <p>Tem certeza que deseja excluir o serviço: &quot;{deleteTarget?.name}&quot;?</p>
            <p>Esta ação não pode ser desfeita.</p>
          </div>
        }
        actions={[
          { label: 'Cancelar', onClick: () => setDeleteTarget(null), variant: 'outline' },
          { label: 'Excluir', onClick: handleConfirmDelete, variant: 'danger' },
        ]}
      />
    </TableWrapper>
  )
}

export default StageServicesTable
